import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, Crown, Zap, Leaf, Sparkles, ArrowRight } from 'lucide-react';
import SharedNav from '@/components/SharedNav';
import { API, useAuth } from '@/App';

const GREEN = '#40916C';
const FOREST = '#1F3A2E';

const FREE_FEATURES = [
  'Core biometrical analyses',
  'Research design tools',
  'Up to 5 saved analyses',
  'Standard result views',
];
const PRO_FEATURES = [
  'Everything in Free',
  'Unlimited saved analyses',
  'Document exports (PDF / DOCX / XLSX)',
  'Molecular & QTL modules',
  'BK Intelligence assistant',
  'Priority compute queue',
];

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Pricing() {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const [config, setConfig] = useState({ enabled: false, plans: [] });
  const [interval, setIntervalSel] = useState('year'); // 'month' | 'year'
  const [busy, setBusy] = useState(false);
  const [celebrate, setCelebrate] = useState(false);

  useEffect(() => {
    axios.get(`${API}/payments/config`)
      .then(({ data }) => setConfig(data))
      .catch(() => setConfig({ enabled: false, plans: [] }));
  }, []);

  const isPro = user?.plan === 'pro';
  const plan = config.plans.find(p => p.interval === interval) || {};
  const monthlyPlan = config.plans.find(p => p.interval === 'month');
  const yearlyPlan = config.plans.find(p => p.interval === 'year');
  const yearlyPerMonth = yearlyPlan ? Math.round(yearlyPlan.amount_display / 12) : null;

  const handleUpgrade = async () => {
    if (!plan.id) return;
    if (!config.enabled) {
      toast.info('Payments are not enabled yet — add your Razorpay keys to go live.');
      return;
    }
    setBusy(true);
    try {
      const ok = await loadRazorpay();
      if (!ok) { toast.error('Could not load the payment window. Check your connection.'); return; }
      const { data } = await axios.post(`${API}/payments/create-order`, { plan_id: plan.id }, { withCredentials: true });
      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: 'BKAgriD',
        description: data.plan.label,
        order_id: data.order_id,
        prefill: { name: data.name || '', email: data.email || '' },
        theme: { color: GREEN },
        handler: async (resp) => {
          try {
            await axios.post(`${API}/payments/verify`, {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            }, { withCredentials: true });
            await checkAuth();
            setCelebrate(true);
          } catch (e) {
            toast.error('We could not verify your payment. If you were charged, contact support.');
          }
        },
        modal: { ondismiss: () => setBusy(false) },
      });
      rzp.on('payment.failed', () => toast.error('Payment failed. Please try again.'));
      rzp.open();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not start checkout. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--page-bg)' }} data-testid="pricing-page">
      <SharedNav />
      <div className="max-w-[1080px] mx-auto px-6 py-14">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-4"
            style={{ background: 'rgba(64,145,108,0.1)', color: GREEN, fontSize: 12, fontWeight: 600, letterSpacing: '0.04em' }}>
            <Leaf size={13} /> PRICING
          </div>
          <h1 className="font-display" style={{ fontSize: 44, color: FOREST, letterSpacing: '-0.02em', lineHeight: 1.1 }}>
            Grow faster with Pro
          </h1>
          <p className="font-body mx-auto mt-3" style={{ fontSize: 16, color: 'var(--text-medium)', maxWidth: 520 }}>
            Start free. Upgrade when you need unlimited analyses, exports and the full molecular toolkit.
          </p>
        </div>

        {/* interval toggle */}
        <div className="flex items-center justify-center gap-3 mb-9" data-testid="billing-toggle">
          <button onClick={() => setIntervalSel('month')} data-testid="toggle-monthly"
            className="px-4 py-1.5 rounded-full transition-all font-body"
            style={{ fontSize: 13, fontWeight: 600, border: '1px solid var(--border-color)',
              background: interval === 'month' ? FOREST : 'white', color: interval === 'month' ? 'white' : 'var(--text-dark)' }}>
            Monthly
          </button>
          <button onClick={() => setIntervalSel('year')} data-testid="toggle-yearly"
            className="px-4 py-1.5 rounded-full transition-all font-body flex items-center gap-2"
            style={{ fontSize: 13, fontWeight: 600, border: '1px solid var(--border-color)',
              background: interval === 'year' ? FOREST : 'white', color: interval === 'year' ? 'white' : 'var(--text-dark)' }}>
            Yearly
            <span style={{ fontSize: 11, background: interval === 'year' ? GREEN : 'rgba(64,145,108,0.15)',
              color: interval === 'year' ? 'white' : GREEN, padding: '1px 7px', borderRadius: 20, fontWeight: 700 }}>
              Save ~17%
            </span>
          </button>
        </div>

        <div className="grid md:grid-cols-2 gap-6 items-stretch">
          {/* Free */}
          <div className="rounded-2xl p-8 bg-white flex flex-col" data-testid="plan-card-free"
            style={{ border: '1px solid var(--border-color)' }}>
            <div className="flex items-center gap-2 mb-1">
              <Zap size={18} style={{ color: 'var(--text-medium)' }} />
              <h3 className="font-display" style={{ fontSize: 22, color: FOREST }}>Free</h3>
            </div>
            <p className="font-body mb-5" style={{ fontSize: 13.5, color: 'var(--text-medium)' }}>For getting started and small studies.</p>
            <div className="mb-6">
              <span className="font-display" style={{ fontSize: 40, color: FOREST }}>₹0</span>
              <span className="font-body" style={{ fontSize: 14, color: 'var(--text-light)' }}> / forever</span>
            </div>
            <ul className="space-y-3 mb-8 flex-1">
              {FREE_FEATURES.map(f => (
                <li key={f} className="flex items-start gap-2.5 font-body" style={{ fontSize: 14, color: 'var(--text-dark)' }}>
                  <Check size={16} style={{ color: GREEN, marginTop: 2, flexShrink: 0 }} /> {f}
                </li>
              ))}
            </ul>
            <button disabled data-testid="free-plan-button"
              className="w-full py-3 rounded-xl font-body"
              style={{ fontSize: 14, fontWeight: 600, background: '#F0EEE8', color: 'var(--text-light)', border: 'none', cursor: 'default' }}>
              {isPro ? 'Included' : 'Your current plan'}
            </button>
          </div>

          {/* Pro */}
          <div className="rounded-2xl p-8 flex flex-col relative overflow-hidden" data-testid="plan-card-pro"
            style={{ border: `2px solid ${GREEN}`, background: 'linear-gradient(180deg,#FFFFFF 0%,#F6FAF7 100%)', boxShadow: '0 12px 40px rgba(31,58,46,0.10)' }}>
            <div className="absolute top-0 right-0 px-3 py-1"
              style={{ background: GREEN, color: 'white', fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', borderBottomLeftRadius: 12 }}>
              MOST POPULAR
            </div>
            <div className="flex items-center gap-2 mb-1">
              <Crown size={18} style={{ color: GREEN }} />
              <h3 className="font-display" style={{ fontSize: 22, color: FOREST }}>Pro</h3>
            </div>
            <p className="font-body mb-5" style={{ fontSize: 13.5, color: 'var(--text-medium)' }}>For breeders shipping real programmes.</p>
            <div className="mb-1">
              <span className="font-display" style={{ fontSize: 40, color: FOREST }}>
                ₹{interval === 'year' ? (yearlyPerMonth ?? 417) : (monthlyPlan?.amount_display ?? 499)}
              </span>
              <span className="font-body" style={{ fontSize: 14, color: 'var(--text-light)' }}> / month</span>
            </div>
            <p className="font-body mb-6" style={{ fontSize: 12.5, color: 'var(--text-light)', minHeight: 18 }}>
              {interval === 'year'
                ? `Billed ₹${yearlyPlan?.amount_display ?? 4999} once a year`
                : 'Billed monthly, cancel anytime'}
            </p>
            <ul className="space-y-3 mb-8 flex-1">
              {PRO_FEATURES.map(f => (
                <li key={f} className="flex items-start gap-2.5 font-body" style={{ fontSize: 14, color: 'var(--text-dark)' }}>
                  <Check size={16} style={{ color: GREEN, marginTop: 2, flexShrink: 0 }} /> {f}
                </li>
              ))}
            </ul>
            <button onClick={handleUpgrade} disabled={busy || isPro} data-testid="upgrade-pro-button"
              className="w-full py-3 rounded-xl font-body transition-all btn-press"
              style={{ fontSize: 15, fontWeight: 700, background: isPro ? '#E3EFE7' : GREEN, color: isPro ? GREEN : 'white',
                border: 'none', cursor: isPro ? 'default' : 'pointer', opacity: busy ? 0.7 : 1 }}>
              {isPro ? '✓ You are on Pro' : busy ? 'Opening checkout…' : `Upgrade to Pro`}
            </button>
            {!config.enabled && !isPro && (
              <p className="font-body text-center mt-3" style={{ fontSize: 11.5, color: 'var(--text-light)' }} data-testid="payments-disabled-note">
                Checkout activates once Razorpay keys are configured.
              </p>
            )}
          </div>
        </div>

        <p className="font-body text-center mt-8" style={{ fontSize: 12.5, color: 'var(--text-light)' }}>
          Secure payments via Razorpay · UPI, cards, netbanking & wallets · Prices in INR
        </p>
      </div>

      {celebrate && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center px-6"
          data-testid="pro-celebration-overlay"
          style={{ background: 'rgba(15,26,20,0.55)', backdropFilter: 'blur(6px)' }}>
          {/* confetti */}
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            {Array.from({ length: 40 }).map((_, i) => {
              const colors = [GREEN, '#F2C14E', '#E76F51', FOREST, '#7DBE9C'];
              return (
                <span key={i} className="pro-confetti" style={{
                  left: `${(i * 2.5) % 100}%`,
                  background: colors[i % colors.length],
                  animationDelay: `${(i % 10) * 0.15}s`,
                  animationDuration: `${2.6 + (i % 5) * 0.4}s`,
                  width: i % 3 === 0 ? 10 : 7,
                  height: i % 3 === 0 ? 6 : 10,
                }} />
              );
            })}
          </div>

          <div className="relative rounded-3xl bg-white text-center px-8 py-10 max-w-[420px] w-full pro-pop"
            style={{ boxShadow: '0 24px 80px rgba(31,58,46,0.35)' }}>
            <div className="mx-auto mb-5 flex items-center justify-center rounded-full"
              style={{ width: 84, height: 84, background: 'linear-gradient(135deg,#40916C,#1F3A2E)', boxShadow: '0 10px 30px rgba(64,145,108,0.4)' }}>
              <Crown size={40} color="white" />
            </div>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full mb-3"
              style={{ background: 'rgba(64,145,108,0.12)', color: GREEN, fontSize: 11.5, fontWeight: 700, letterSpacing: '0.05em' }}>
              <Sparkles size={13} /> PAYMENT VERIFIED
            </div>
            <h2 className="font-display" data-testid="pro-celebration-title"
              style={{ fontSize: 30, color: FOREST, letterSpacing: '-0.02em', lineHeight: 1.15 }}>
              Welcome to Pro 🎉
            </h2>
            <p className="font-body mx-auto mt-3 mb-7" style={{ fontSize: 14.5, color: 'var(--text-medium)', maxWidth: 320 }}>
              Your account is upgraded. Unlimited analyses, exports and the full
              molecular toolkit are now unlocked.
            </p>
            <button onClick={() => { setCelebrate(false); navigate('/dashboard'); }}
              data-testid="celebration-continue-button"
              className="w-full py-3 rounded-xl font-body transition-all btn-press inline-flex items-center justify-center gap-2"
              style={{ fontSize: 15, fontWeight: 700, background: GREEN, color: 'white', border: 'none', cursor: 'pointer' }}>
              Go to dashboard <ArrowRight size={17} />
            </button>
          </div>

          <style>{`
            @keyframes proConfettiFall {
              0% { transform: translateY(-20vh) rotate(0deg); opacity: 1; }
              100% { transform: translateY(110vh) rotate(720deg); opacity: 0.9; }
            }
            .pro-confetti {
              position: absolute; top: -20px; border-radius: 2px;
              animation-name: proConfettiFall; animation-timing-function: linear;
              animation-iteration-count: infinite;
            }
            @keyframes proPop {
              0% { transform: scale(0.8); opacity: 0; }
              60% { transform: scale(1.03); opacity: 1; }
              100% { transform: scale(1); opacity: 1; }
            }
            .pro-pop { animation: proPop 0.45s cubic-bezier(0.22,1,0.36,1) both; }
          `}</style>
        </div>
      )}
    </div>
  );
}
